import { Injectable } from '@angular/core';
import { from, Observable, Subject, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpParams, HttpHeaders} from '@angular/common/http';
import { catchError, tap} from 'rxjs/operators';

import { IReleaseNoteResponse, IMonthResponse, ITransferNote, ITransferNoteResponse } from '../models/release-note.model';
import { UserManager } from 'oidc-client';
import { AppConfig } from './app.config';

@Injectable({
  providedIn: 'root'
})
export class ReleaseNoteService {

  constructor(private http: HttpClient) {

    const stsSettings = {
      authority: AppConfig.settings.ID4.AuthAddress,
      client_id: AppConfig.settings.ID4.Client,
      redirect_uri: AppConfig.settings.ID4.Redirect,
      scope: 'openid RNapi',
      response_type: 'code',
      post_logout_redirect_uri: AppConfig.settings.ID4.Post_Logout,
      loadUserInfo: false
    };

    this._userManager = new UserManager(stsSettings);

  }
  private noteUrl = 'api/releasenote';
  private xferUrl = 'api/transfernote';
  private _userManager: UserManager;

  private transferNote: ITransferNote;
  private transferNoteSource = new Subject<ITransferNote>();
  transferNote$ = this.transferNoteSource.asObservable();

  noCacheHeader = {'Cache-Control': 'no-cache', Pragma: 'no-cache'};

  pingTransferNote() {
    this.transferNoteSource.next(this.transferNote);
  }

  setTransferNote(note: ITransferNote) {
    this.transferNote = note;
    this.transferNoteSource.next(this.transferNote);
  }

  getMonths(appID: number, searchTerm = ''): Observable<IMonthResponse> {
    const url = `api/Month/${appID}`;
    const params = new HttpParams().set('search', searchTerm);
    return this.http.get<IMonthResponse>(url, { headers: new HttpHeaders(this.noCacheHeader), params})
      .pipe(tap(data => {}), catchError(this.handleError));
  }

  getReleaseNotes(appID: number): Observable<IReleaseNoteResponse> {
    const url = `${this.noteUrl}/${appID}`;
    return this.http.get<IReleaseNoteResponse>(url)
      .pipe(tap(data => {}), catchError(this.handleError));
  }

  getXFerbyNoteID(noteID: number): Observable<ITransferNoteResponse> {
    const url = `${this.xferUrl}/${noteID}`;
    return this.http.get<ITransferNoteResponse>(url, { headers: new HttpHeaders(this.noCacheHeader) })
      .pipe(tap(data => {}), catchError(this.handleError));
  }

  getNotebyDate(appID: number, date: string[]): Observable<IReleaseNoteResponse> {
    const url = `${this.noteUrl}/${appID}/date/`;
    const params = new HttpParams().set('year', date[0]).set('month', date[1]).set('date', date[2]);
    return this.http.get<IReleaseNoteResponse>(url, { headers: new HttpHeaders(this.noCacheHeader), params })
      .pipe(tap(data => {}), catchError(this.handleError));
  }

  getNotebyDateVersion(appID: number, date: string[], version: string): Observable<IReleaseNoteResponse> {
    const url = `${this.noteUrl}/${appID}/${version}/date/`;
    const params = new HttpParams().set('year', date[0]).set('month', date[1]).set('date', date[2]);
    return this.http.get<IReleaseNoteResponse>(url, { headers: new HttpHeaders(this.noCacheHeader), params })
      .pipe(tap(data => {}), catchError(this.handleError));
  }

  getMonthlyNotes(appID: number, monthYear: number, searchTerm = ''): Observable<IReleaseNoteResponse> {
    const params = new HttpParams().set('search', searchTerm);
    return this.http.get<IReleaseNoteResponse>(`${this.noteUrl}/${appID}/${monthYear}`,
            { headers: new HttpHeaders(this.noCacheHeader), params})
            .pipe(tap(data => {}), catchError(this.handleError));
  }

  setReleaseNotes(notesForm): Observable<IReleaseNoteResponse> {
    return from(this._userManager.getUser().then(user => {
      if (!(user == null) && !user.expired) {
        const head = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.access_token}`
        });
        return this.http.post<any>(this.noteUrl, notesForm, { headers: head }).toPromise();
      } else {
        this._userManager.signinRedirect();
      }
    }));
  }

  updateReleaseNotes(notesForm): Observable<IReleaseNoteResponse> {
    return from(this._userManager.getUser().then(user => {
      if (!(user == null) && !user.expired) {
        const head = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.access_token}`
        });
        return this.http.patch<any>(this.noteUrl, notesForm, { headers: head }).toPromise();
      } else {
        this._userManager.signinRedirect();
      }
    }));
  }

  deleteReleaseNotes(noteID): Observable<IReleaseNoteResponse> {
    return from(this._userManager.getUser().then(user => {
      if (!(user == null) && !user.expired) {
        const head = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.access_token}`
        });
        const params = new HttpParams().set('noteId', noteID);
        return this.http.delete<any>(this.noteUrl, { headers: head, params }).toPromise();
      } else {
        this._userManager.signinRedirect();
      }
    }));
  }

  private handleError(err: HttpErrorResponse) {
    let errorMessage = '';
    if (err.error instanceof ErrorEvent) {
      errorMessage = `An error occurred: ${err.error.message}`;
    } else {
      errorMessage = `Server returned code: ${err.status}, error message is: ${err.message}`;
    }
    console.error(errorMessage);
    return throwError(errorMessage);
  }

}
